import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Flex, Heading, Text, Button, TextField, Checkbox, useToast } from '@chatsdet/ui';
import { DotsSixVertical, Trash, Plus } from '@phosphor-icons/react';
import {
  useChecklistItems,
  useCreateChecklistItem,
  useUpdateChecklistItem,
  useDeleteChecklistItem,
  useReorderChecklistItem,
  type CardChecklistItem,
} from '../hooks/useCards';

export function ChecklistPanel({ cardId }: { cardId: string }) {
  const { data: items = [] } = useChecklistItems(cardId);
  const create = useCreateChecklistItem(cardId);
  const update = useUpdateChecklistItem(cardId);
  const remove = useDeleteChecklistItem(cardId);
  const reorder = useReorderChecklistItem(cardId);
  const { toast } = useToast();

  const [newText, setNewText] = React.useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const total = items.length;
  const completed = items.filter((it) => it.checked).length;
  const progressPct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const handleAdd = () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    create.mutate(trimmed, {
      onSuccess: () => setNewText(''),
      onError: () => toast.error('Failed to add item'),
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Compute neighbor IDs at the *new* position to send to the server.
    const beforeId = newIndex > 0 ? reordered[newIndex - 1].id : null;
    const afterId = newIndex < reordered.length - 1 ? reordered[newIndex + 1].id : null;

    reorder.mutate({
      id: moved.id,
      beforeId,
      afterId,
      orderedIds: reordered.map((it) => it.id),
    });
  };

  return (
    <Flex direction="column" gap="sm" className="mt-6 border-t pt-4">
      <Flex justify="between" align="center">
        <Heading level={4}>Checklist</Heading>
        {total > 0 && (
          <Text size="xs" className="text-muted-foreground">
            {completed} / {total}
          </Text>
        )}
      </Flex>

      {total > 0 && (
        <div className="h-1 w-full overflow-hidden rounded bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
          <Flex direction="column" gap="xs">
            {items.map((item) => (
              <ChecklistRow
                key={item.id}
                item={item}
                onToggle={(checked) => update.mutate({ id: item.id, checked })}
                onRename={(text) => {
                  if (text.trim() && text !== item.text) {
                    update.mutate({ id: item.id, text: text.trim() });
                  }
                }}
                onDelete={() => remove.mutate(item.id)}
              />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>

      {total === 0 && (
        <Text size="sm" className="text-muted-foreground">
          No items yet.
        </Text>
      )}

      <Flex gap="xs" align="center" className="mt-2">
        <TextField
          aria-label="New checklist item"
          value={newText}
          onChange={setNewText}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Add an item…"
          className="flex-1"
        />
        <Button
          variant="ghost"
          size="sm"
          onPress={handleAdd}
          isDisabled={!newText.trim() || create.isPending}
          iconLeft={<Plus size={14} />}
        >
          Add
        </Button>
      </Flex>
    </Flex>
  );
}

interface ChecklistRowProps {
  item: CardChecklistItem;
  onToggle: (checked: boolean) => void;
  onRename: (text: string) => void;
  onDelete: () => void;
}

function ChecklistRow({ item, onToggle, onRename, onDelete }: ChecklistRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [text, setText] = React.useState(item.text);
  const [editing, setEditing] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setText(item.text);
  }, [item.text, editing]);

  const commit = () => {
    setEditing(false);
    onRename(text);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-2 rounded border border-transparent px-1 py-1 hover:border-border hover:bg-muted/30"
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <DotsSixVertical size={16} weight="bold" />
      </button>
      <Checkbox isSelected={item.checked} onChange={onToggle} />
      {editing ? (
        <TextField
          aria-label="Edit item"
          value={text}
          onChange={setText}
          onBlur={commit}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') {
              setText(item.text);
              setEditing(false);
            }
          }}
          autoFocus
          className="flex-1"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={
            'flex-1 truncate text-left text-sm ' +
            (item.checked ? 'text-muted-foreground line-through opacity-60' : '')
          }
        >
          {item.text}
        </button>
      )}
      <Button
        variant="icon"
        iconOnly
        size="sm"
        tooltip="Delete"
        onPress={onDelete}
        className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
      >
        <Trash size={14} />
      </Button>
    </div>
  );
}
